/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useContext } from 'react'
import ky from 'ky'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAngleLeft,
  faAngleRight,
  faAnglesLeft,
  faAnglesRight,
  faCalendarDays,
  faDirections,
  faFire,
  faGear,
  faIdCard,
  faLocationDot,
  faPen,
  faPhone,
  faSpinner,
  faSync,
  faTableColumns,
  faTag
} from '@fortawesome/free-solid-svg-icons'
import IFireplace from '../../server/src/Interfaces/IFireplace'
import IAddress from '../../server/src/Interfaces/IAddress'
import IAddressCategory from '../../server/src/Interfaces/IAddressCategory'
import IAddressType from '../../server/src/Interfaces/IAddressType'
import EditCustomer from './EditCustomer'
import { GlobalContext } from '../App'
import { updateAddressCategories, updateAddressTypes, updateFireplaces } from '../Modules/Data'
import { API_URL } from '../Globals'
import AllCustomerChartModal from './AllCustomerChartModal'
import { AppPermission, hasPermission } from '../Permissions/PermissionDefinitions'

const Customers: React.FC = () => {
  const { globalVariable, setGlobalVariable } = useContext(GlobalContext)
  const [searchText, setSearchText] = useState('')
  const [addresses, setAddresses] = useState<IAddress[]>([])
  const [selectedAddress, setSelectedAddress] = useState<IAddress | undefined>(undefined)
  const [fireplaceIds, setFireplaceIds] = useState<IFireplace[]>([])
  const [addressCategories, setAddressCategories] = useState<IAddressCategory[]>([])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [addressTypes, setAddressTypes] = useState<IAddressType[]>([])
  const [addressCategoryId, setAddressCategoryId] = useState<number>(0)
  const [loading, setLoading] = useState(false)
  const [addressTotalCount, setAddressTotalCount] = useState(0)
  const [sweepModalCurrentYear, setSweepModalCurrentYear] = useState(false)
  const [sweepModalNextYear, setSweepModalNextYear] = useState(false)
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear())
  const [nextYear, setNextYear] = useState(new Date().getFullYear() + 1)
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const isSearchMode = searchText.trim().length > 0
  const totalPages = Math.ceil(addressTotalCount / perPage)
  const [viewMode, setViewMode] = useState<'card' | 'table'>("card")
  const canEditAddresses = hasPermission(globalVariable.permissions, AppPermission.ADDRESS_EDIT)

  useEffect(() => {
    if (!globalVariable.addressToEdit || !canEditAddresses) return

    setSelectedAddress(globalVariable.addressToEdit)
    setGlobalVariable(previous => ({ ...previous, addressToEdit: undefined }))
  }, [canEditAddresses, globalVariable.addressToEdit, setGlobalVariable])

  // Table columns config
  const tableColumns = [
    { key: 'name', label: 'Nimi' },
    { key: 'address', label: 'Osoite' },
    { key: 'category', label: 'Kategoria' },
    { key: 'phoneNumbers', label: 'Puhelinnumerot' },
    { key: 'fireplaces', label: 'Tulisijat' },
    { key: 'lastSweep', label: 'Viimeksi nuohottu' },
    { key: 'nextSweep', label: 'Seuraava nuohous' },
    { key: 'additionalInfo', label: 'Lisätiedot' }, // Optional column
    { key: 'actions', label: 'Toiminnot' }
  ];
  // Exclude 'additionalInfo' from default visible columns
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('customerTableColumns');
    return saved
      ? JSON.parse(saved)
      : tableColumns.filter(col => col.key !== 'additionalInfo').map(col => col.key);
  });
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  useEffect(() => {
    localStorage.setItem('customerTableColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          updateFireplaces(globalVariable.sessionToken, setFireplaceIds),
          updateAddressCategories(globalVariable.sessionToken, setAddressCategories),
          updateAddressTypes(globalVariable.sessionToken, setAddressTypes)
        ])
      } catch {
        console.error('Kohdetietojen lataus epäonnistui.')
      }
    }
    void fetchData()
  }, [globalVariable.sessionToken])

  useEffect(() => {
    const controller = new AbortController()
    const delay = isSearchMode ? 350 : 0
    const timeout = window.setTimeout(async () => {
      setLoading(true)
      try {
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${globalVariable.sessionToken}`,
          'Token': localStorage.getItem('token') as string
        }
        const response = isSearchMode
          ? await ky.post(`${API_URL}/api/address/search`, {
            json: { text: searchText.trim(), addressCategoryId, page: currentPage, perPage },
            headers,
            signal: controller.signal
          }).json<{ addresses: IAddress[], totalCount: number }>()
          : await ky.get(`${API_URL}/api/address/all/${addressCategoryId}?page=${currentPage}&perPage=${perPage}`, {
            headers,
            signal: controller.signal
          }).json<{ addresses: IAddress[], totalCount: number }>()

        setAddresses(response.addresses)
        setAddressTotalCount(response.totalCount)
      } catch {
        if (!controller.signal.aborted) {
          console.error('Kohdelistan päivitys epäonnistui.')
          setAddresses([])
          setAddressTotalCount(0)
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }, delay)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [addressCategoryId, currentPage, globalVariable.sessionToken, isSearchMode, refreshTrigger, searchText])

  const getFireplaceName = (id: number) =>
    fireplaceIds.find(item => item.id === id)?.name ?? null

  const openMapsLink = (address: IAddress) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address.address}, ${address.zip} ${address.city}`)}`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const handleCategoryChange = (value: string) => {
    const categoryId = Number(value);
    setAddressCategoryId(categoryId);
    setCurrentPage(1);
  };

  const isValidPhoneNumber = (number: string) => {
    // Only allow digits, spaces, and an optional leading "+"
    if (number.trim() === '') return true;
    const phoneRegex = /^\+?[\d ]+$/;
    return phoneRegex.test(number);
  };

  const clearSelection = () => setSelectedAddress(undefined)

  const getAddressCategoryName = (id: number) =>
    addressCategories.find(item => item.id === id)?.name ?? null

  const filteredData = addresses

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  const PaginationControls = () => {
    return (
      <div className="flex items-center justify-center gap-2" aria-label="Sivutus">
        <button
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-blue-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          title="Ensimmäinen sivu"
        >
          <FontAwesomeIcon icon={faAnglesLeft} />
        </button>
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-blue-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          title="Edellinen sivu"
        >
          <FontAwesomeIcon icon={faAngleLeft} />
        </button>
        <span
          className="flex h-11 min-w-[5.5rem] items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-bold text-white shadow-sm"
        >
          {currentPage} / {totalPages || 1}
        </span>
        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-blue-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          title="Seuraava sivu"
        >
          <FontAwesomeIcon icon={faAngleRight} />
        </button>
        <button
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || totalPages === 0}
          className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 text-gray-500 transition hover:bg-blue-100 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
          title="Viimeinen sivu"
        >
          <FontAwesomeIcon icon={faAnglesRight} />
        </button>
      </div>
    );
  };

  return (
    <div className="container mx-auto mb-10 px-3 sm:px-6 lg:px-8">
      <AllCustomerChartModal isOpen={sweepModalCurrentYear} onClose={() => setSweepModalCurrentYear(false)} year={currentYear} />
      <AllCustomerChartModal isOpen={sweepModalNextYear} onClose={() => setSweepModalNextYear(false)} year={nextYear} />
      <div className="mb-5 rounded-2xl bg-white py-3 sm:py-4">
        <div className="grid gap-3 md:grid-cols-2">
          <label className="relative block">
            <span className="sr-only">Etsi kohteita</span>
            <input
              type="search"
              value={searchText}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchText(e.target.value)
              }}
              placeholder="Etsi nimellä, osoitteella tai puhelimella"
              className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-700 shadow-sm transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </label>
          <select
            value={addressCategoryId}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="h-12 w-full rounded-xl border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Suodata kategorian mukaan"
          >
            <option value={0}>Kaikki kategoriat</option>
            {addressCategories.map((category) => (
              <option key={category.id} value={category.id === 0 ? "" : category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-500">
            <span className="font-bold text-blue-600">{currentPage} / {totalPages || 1}</span> sivua
            <span className="mx-2 text-gray-300">•</span>
            <span className="font-medium text-gray-700">{addressTotalCount} kohdetta</span>
          </p>
          <div className="flex gap-2">
          <button
            onClick={() => setRefreshTrigger(value => value + 1)}
            className="flex h-11 flex-1 items-center justify-center whitespace-nowrap rounded-xl bg-blue-50 px-4 font-medium text-blue-600 transition hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex-none"
          >
            <FontAwesomeIcon icon={faSync} className="mr-2" />
            Päivitä
          </button>
          <button
            onClick={() => setViewMode(viewMode === 'card' ? 'table' : 'card')}
            className="flex h-11 flex-1 items-center justify-center whitespace-nowrap rounded-xl bg-gray-100 px-4 font-medium text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:flex-none"
            title={viewMode === 'card' ? 'Näytä taulukkona' : 'Näytä kortteina'}
          >
            <FontAwesomeIcon
              icon={viewMode === 'card' ? faTableColumns : faIdCard}
              className="mr-2"
            />
            {viewMode === 'card' ? 'Taulu' : 'Kortti'}
          </button>
          {viewMode === 'table' && (
            <div className="relative flex-1 sm:flex-none">
              <button
                onClick={() => setShowColumnSelector(!showColumnSelector)}
                className="flex h-11 w-full items-center justify-center whitespace-nowrap rounded-xl bg-gray-100 px-4 font-medium text-gray-700 transition hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Valitse sarakkeet"
              >
                <FontAwesomeIcon icon={faTableColumns} className="mr-2" />
                Sarakkeet
              </button>
              {showColumnSelector && (
                <div className="absolute z-20 bg-white border border-gray-300 rounded-lg shadow-lg p-4 mt-2 min-w-[180px] transform sm:left-1/2 sm:right-auto sm:-translate-x-1/2">
                  <div className="mb-2 font-semibold text-gray-700 text-xs">Näytettävät sarakkeet</div>
                  {tableColumns.map(col => (
                    <label key={col.key} className="flex items-center mb-1 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => {
                          setVisibleColumns((prev: string[]) => {
                            const updated = prev.includes(col.key)
                              ? prev.length === 1 ? prev : prev.filter((k: string) => k !== col.key)
                              : [...prev, col.key];
                            localStorage.setItem('customerTableColumns', JSON.stringify(updated));
                            return updated;
                          });
                        }}
                        className="mr-2"
                        disabled={visibleColumns.length === 1 && visibleColumns.includes(col.key)}
                      />
                      {col.label}
                    </label>
                  ))}
                  <button
                    className="mt-2 mr-2 px-3 py-1 bg-gray-300 text-gray-700 rounded text-xs hover:bg-gray-400"
                    onClick={() => {
                      // Reset to default columns (all except 'additionalInfo')
                      const defaultCols = tableColumns.filter(col => col.key !== 'additionalInfo').map(col => col.key);
                      setVisibleColumns(defaultCols);
                      localStorage.setItem('customerTableColumns', JSON.stringify(defaultCols));
                    }}
                  >
                    Oletus
                  </button>
                  <button
                    className="mt-2 px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                    onClick={() => setShowColumnSelector(false)}
                  >
                    Sulje
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      {!selectedAddress && filteredData.length > 0 && (
        <div className="mb-6"><PaginationControls /></div>
      )}
      {selectedAddress && <EditCustomer selectedAddress={selectedAddress} clearSelection={clearSelection} updateAddressListFn={() => setRefreshTrigger(value => value + 1)} />}

      {loading && (
        <div className="flex justify-center items-center">
          <FontAwesomeIcon icon={faSpinner} className="mr-2 text-blue-500 animate-spin" />
          <span className="text-blue-500">Ladataan...</span>
        </div>
      )}
      {!selectedAddress && filteredData.length === 0 && addresses.length > 0 && (
        <div className="flex justify-center items-center">
          <span className="text-gray-500">Ei tuloksia</span>
        </div>
      )}
      {!selectedAddress && filteredData.length > 0 && !loading && (
        viewMode === 'card' ? (
          <div className="grid grid-cols-1 items-stretch gap-5 xl:grid-cols-2 2xl:grid-cols-3">
            {filteredData.map((item: any, index: number) => {
              const daysUntilNextSweep = item.nextSweepDate
                ? Math.ceil((new Date(item.nextSweepDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                : null
              const isOverdue = daysUntilNextSweep !== null && daysUntilNextSweep < 0
              const isDueSoon = daysUntilNextSweep !== null && daysUntilNextSweep >= 0 && daysUntilNextSweep <= 30
              const lastSweepDays = item.lastSweepDate
                ? Math.ceil((new Date().getTime() - new Date(item.lastSweepDate).getTime()) / (1000 * 60 * 60 * 24))
                : null
              const customerName = item.firstname || item.lastname
                ? `${item.firstname} ${item.lastname}`.trim()
                : 'Nimetön'

              return (
                <article key={item._id ?? index} className="h-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex h-full flex-col p-4 sm:p-5">
                    <div className="grid grid-cols-[minmax(0,1fr)_9.5rem] gap-3 sm:grid-cols-[minmax(0,1fr)_11rem] sm:gap-5">
                      <div className="min-w-0 space-y-2.5">
                        <h3 className="truncate text-xl font-bold text-blue-600">{customerName}</h3>
                        <p className="flex items-start gap-2 text-sm text-gray-700">
                          <FontAwesomeIcon icon={faLocationDot} className="mt-0.5 w-4 flex-none text-gray-500" />
                          <span>{item.address || 'Ei osoitetta'}{item.zip && item.city ? `, ${item.zip} ${item.city}` : ''}</span>
                        </p>
                        <p className="flex items-center gap-2 text-sm text-gray-700">
                          <FontAwesomeIcon icon={faTag} className="w-4 flex-none text-gray-500" />
                          <span><strong>Kategoria:</strong> {getAddressCategoryName(item.addressCategoryId) || 'Ei määritelty'}</span>
                        </p>
                        <div className="flex items-start gap-2 text-sm">
                          <FontAwesomeIcon icon={faPhone} className="mt-0.5 w-4 flex-none text-gray-500" />
                          <div className="flex min-w-0 flex-wrap gap-x-2 gap-y-1">
                            {item.phoneNumbers.length > 0 ? item.phoneNumbers.map((number: string, idx: number) => (
                              <React.Fragment key={`${number}-${idx}`}>
                                <a href={`tel:${number}`} className={`${isValidPhoneNumber(number) ? 'text-blue-600' : 'font-bold text-red-600'} break-all hover:underline`}>{number}</a>
                                {!isValidPhoneNumber(number) && <span className="rounded bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">Virheellinen</span>}
                              </React.Fragment>
                            )) : <span className="text-gray-400">Ei puhelinnumeroa</span>}
                          </div>
                        </div>
                      </div>

                      <div className={`self-start overflow-hidden rounded-xl text-center ${isOverdue ? 'bg-red-50' : isDueSoon ? 'bg-amber-50' : 'bg-blue-50'}`}>
                        <div className="px-2 py-3">
                          <p className={`text-xs font-medium ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-700' : 'text-gray-600'}`}>Seuraava nuohous</p>
                          <p className={`mt-1 text-lg font-bold ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-700' : 'text-gray-600'}`}>
                            {item.nextSweepDate ? new Date(item.nextSweepDate).toLocaleDateString('fi-FI') : 'Ei sovittua'}
                          </p>
                        </div>
                        {isOverdue && (
                          <div className="bg-red-600 px-2 py-2 text-xs font-bold text-white">Myöhässä {Math.abs(daysUntilNextSweep as number)} pv</div>
                        )}
                        {isDueSoon && (
                          <div className="bg-amber-500 px-2 py-2 text-xs font-bold text-white">{daysUntilNextSweep} pv jäljellä</div>
                        )}
                      </div>
                    </div>

                    <div className={`mt-4 grid gap-2.5 ${canEditAddresses ? 'grid-cols-2' : 'grid-cols-1'}`}>
                      {canEditAddresses && (
                        <button
                          onClick={() => setSelectedAddress(item)}
                          className="flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          <FontAwesomeIcon icon={faPen} />
                          Muokkaa
                        </button>
                      )}
                      <button
                        onClick={() => openMapsLink(item)}
                        className="flex min-h-12 items-center justify-center gap-2 rounded-xl border-2 border-blue-600 bg-white px-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        <FontAwesomeIcon icon={faDirections} />
                        Navigointi
                      </button>
                    </div>

                    <section className="mt-5 border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-bold text-gray-700">Tulisijat</h4>
                      <div className="mt-2 divide-y divide-gray-100">
                        {item.selectedFireplaceIds.length > 0 ? item.selectedFireplaceIds.map((fireplace: any) => (
                          <div key={fireplace.id} className="flex min-h-10 items-center gap-3 py-2 text-sm text-gray-700">
                            <FontAwesomeIcon icon={faFire} className="w-4 text-orange-500" />
                            <span className="min-w-0 flex-1">{getFireplaceName(fireplace.id) || 'Tuntematon tulisija'}</span>
                            <span className="rounded-lg bg-gray-100 px-2 py-1 text-xs font-bold text-gray-700">{fireplace.count} KPL</span>
                          </div>
                        )) : <p className="py-2 text-sm text-gray-400">Ei merkittyjä tulisijoja</p>}
                      </div>
                    </section>

                    <section className="border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-bold text-gray-700">Lisätiedot</h4>
                      <p className="mt-2 rounded-xl bg-gray-100 px-3 py-2.5 text-sm leading-relaxed text-gray-600">
                        {item.additionalInfo?.trim() || 'Ei lisätietoja'}
                      </p>
                    </section>

                    <div className="mt-auto grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200 pt-4 text-sm">
                      <div className="pr-3">
                        <p className="flex items-center gap-2 font-bold text-gray-700"><FontAwesomeIcon icon={faCalendarDays} className="text-gray-500" />Viimeksi nuohottu</p>
                        {item.lastSweepDate ? (
                          <>
                            <p className="mt-2 font-semibold text-gray-800">{new Date(item.lastSweepDate).toLocaleDateString('fi-FI')}</p>
                            <p className="mt-0.5 text-xs text-gray-400">{lastSweepDays} pv sitten</p>
                          </>
                        ) : <p className="mt-2 text-gray-400">Ei tietoa</p>}
                      </div>
                      <div className="pl-3">
                        <p className="flex items-center gap-2 font-bold text-gray-700"><FontAwesomeIcon icon={faCalendarDays} className="text-gray-500" />Seuraava nuohous</p>
                        {item.nextSweepDate ? (
                          <>
                            <p className={`mt-2 font-semibold ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-700' : 'text-gray-800'}`}>{new Date(item.nextSweepDate).toLocaleDateString('fi-FI')}</p>
                            <p className={`mt-0.5 text-xs font-medium ${isOverdue ? 'text-red-600' : isDueSoon ? 'text-amber-700' : 'text-gray-400'}`}>
                              {isOverdue ? `Myöhässä ${Math.abs(daysUntilNextSweep as number)} pv` : `${daysUntilNextSweep} pv jäljellä`}
                            </p>
                          </>
                        ) : <p className="mt-2 text-gray-400">Ei sovittua</p>}
                      </div>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow">
              <thead>
                <tr>
                  {tableColumns.filter(col => visibleColumns.includes(col.key)).map(col => (
                    <th key={col.key} className="px-4 py-2 border-b text-left text-xs font-bold text-gray-700">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item: any, index: number) => {
                  const daysUntilNextSweep = item.nextSweepDate
                    ? Math.ceil((new Date(item.nextSweepDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : null;
                  return (
                    <tr key={index} className="border-b hover:bg-gray-50 transition-colors duration-150 ease-in-out">
                      {visibleColumns.includes('name') && (
                        <td className="px-4 py-2 text-xs text-blue-700 font-semibold">
                          {item.firstname || item.lastname ? `${item.firstname} ${item.lastname}`.trim() : "Nimetön"}
                        </td>
                      )}
                      {visibleColumns.includes('address') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.address}{item.zip && item.city ? `, ${item.zip} ${item.city}` : ''}
                        </td>
                      )}
                      {visibleColumns.includes('category') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {getAddressCategoryName(item.addressCategoryId)}
                        </td>
                      )}
                      {visibleColumns.includes('phoneNumbers') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.phoneNumbers.map((number: string, idx: number) => (
                            <span key={idx} className="mr-2">
                              <a href={`tel:${number}`} className={`${isValidPhoneNumber(number) ? 'text-blue-600' : 'text-red-600 font-bold'} hover:underline`}>{number}</a>
                              {!isValidPhoneNumber(number) && <span className="bg-red-500 text-white text-xs font-bold ml-1 px-2 py-0.5 rounded">Virheellinen!</span>}
                            </span>
                          ))}
                        </td>
                      )}
                      {visibleColumns.includes('fireplaces') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.selectedFireplaceIds.map((fp: any, idx: number) => (
                            <React.Fragment key={fp.id}>
                              <span className="block p-2">
                                {getFireplaceName(fp.id)}
                                <span className="ml-2 bg-gray-200 text-black px-0.5 py-0.5 text-xs rounded-full border border-gray-300">{fp.count}</span>
                              </span>
                              {idx < item.selectedFireplaceIds.length - 1 && <hr />}
                            </React.Fragment>
                          ))}
                        </td>
                      )}
                      {visibleColumns.includes('lastSweep') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.lastSweepDate ? (
                            <span>{new Date(item.lastSweepDate).toLocaleDateString('fi-FI')}<br />
                              <span className="text-gray-500">({Math.ceil((new Date().getTime() - new Date(item.lastSweepDate).getTime()) / (1000 * 60 * 60 * 24))} pv sitten)</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">Ei tietoa</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.includes('nextSweep') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.nextSweepDate ? (
                            <span>{new Date(item.nextSweepDate).toLocaleDateString('fi-FI')}<br />
                              <span className="text-gray-500">({daysUntilNextSweep} pv)</span>
                            </span>
                          ) : (
                            <span className="text-gray-400">Ei sovittua</span>
                          )}
                        </td>
                      )}
                      {visibleColumns.includes('additionalInfo') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {item.additionalInfo && item.additionalInfo.length > 0
                            ? (item.additionalInfo.length > 200
                              ? `${item.additionalInfo.slice(0, 200)}...`
                              : item.additionalInfo)
                            : <span className="text-gray-400">–</span>
                          }
                        </td>
                      )}
                      {visibleColumns.includes('actions') && (
                        <td className="px-4 py-2 text-xs text-gray-700">
                          {canEditAddresses && (
                            <button onClick={() => setSelectedAddress(item)} className="min-h-10 min-w-10 text-gray-500 hover:text-blue-600 mr-1" title="Muokkaa">
                              <FontAwesomeIcon icon={faGear} className="text-lg" />
                            </button>
                          )}
                          <button onClick={() => openMapsLink(item)} className="min-h-10 min-w-10 text-gray-500 hover:text-blue-600" title="Navigointi">
                            <FontAwesomeIcon icon={faDirections} className="text-lg" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
      {/* Pagination controls below content */}
      {!selectedAddress && filteredData.length > 0 && (
        <div className="mt-6 pb-8 sm:mt-8">
          <PaginationControls />
        </div>
      )}
    </div>
  )
}

export default Customers
